import React, { useCallback, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import NumberPad from 'shared/components/vxl/NumberPad/NumberPad';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import theme from 'shared/styles/theme';

interface MeatBallProps {
  isActive: boolean;
}

export interface PinProps {
  /**
   * Unique identifier for the number of meatballs.
   */
  pinLength: number;
  /**
   * Handles submit button for numpad.
   */
  handleSubmit: () => void;
  /**
   * Stores the pin.
   */
  pin: string;
  /**
   * Function to set pin.
   */
  setPin: (pin: string) => void;
  /**
   * Submit button label.
   */
  numPadSubmitButtonLabel: string;
  /**
   * Flag for error.
   */
  hasError?: boolean;
  /**
   * Error message.
   */
  errorMessage?: string;
  /**
   * Button loading.
   */
  numPadSubmitButtonLoading?: boolean;
}

const Meatball = ({ isActive }: MeatBallProps) => (
  <View
    testID="meatball"
    style={[style.meatball, isActive && style.meatballActive]}
  />
);

const DELETE = 'DELETE';

const Pin = ({
  pinLength,
  handleSubmit,
  pin,
  setPin,
  numPadSubmitButtonLabel = '',
  hasError,
  errorMessage,
  numPadSubmitButtonLoading,
}: PinProps) => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  const meatBallsToRender = Array.from({ length: pinLength }, (_, index) => ({
    id: index,
  }));

  const handleButtonPress = useCallback(
    (key: string) => {
      if (key === DELETE) {
        const newPin = pin.slice(0, -1);
        setPin(newPin);
        setIsSubmitDisabled(newPin.length !== 4);
      } else {
        if (pin.length < 4) {
          const newPin = pin + key;
          setPin(newPin);
          if (newPin.length === 4) {
            setIsSubmitDisabled(false);
          }
        }
      }
    },
    [pin, setPin],
  );

  return (
    <View style={style.container}>
      <View style={style.viewContainer}>
        <View style={style.meatballContainer}>
          {meatBallsToRender.map((meatball, index) => (
            <Meatball key={meatball.id} isActive={index < pin.length} />
          ))}
        </View>
        {hasError ? (
          <View style={style.errorMessageContainer}>
            <Icon
              iconKey="STATUS_ERROR"
              iconColor={theme.color.status.negative}
              iconSize={theme.spacing.m}
            />
            <BodyXSText
              style={style.errorMessage}
              testID="error-msg"
              numberOfLines={0}
            >
              {hasError ? errorMessage : null}
            </BodyXSText>
          </View>
        ) : null}
      </View>
      <View style={style.containerNumpad}>
        <NumberPad
          submitButtonLabel={numPadSubmitButtonLabel}
          submitDisabled={isSubmitDisabled}
          handleButtonPress={handleButtonPress}
          handleSubmit={handleSubmit}
          submitButtonLoading={numPadSubmitButtonLoading}
        />
      </View>
    </View>
  );
};

const style = StyleSheet.create({
  container: { backgroundColor: theme.color.background.primary, flex: 1 },
  containerNumpad: {
    flexBasis: 330,
    width: '100%',
  },
  errorMessage: {
    color: theme.color.status.negative,
  },
  errorMessageContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  meatball: {
    backgroundColor: 'transparent',
    borderColor: theme.color.interactive.primary,
    borderRadius: theme.spacing.m,
    borderWidth: 2,
    height: 16,
    width: 16,
  },
  meatballActive: {
    backgroundColor: theme.color.interactive.primary,
  },
  meatballContainer: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.l,
    justifyContent: 'center',
    width: '100%',
  },
  viewContainer: {
    flex: 1,
    flexShrink: 1,
    gap: theme.spacing.s,
    justifyContent: 'center',
    padding: theme.spacing.m,
    width: '100%',
  },
});

export default Pin;
